import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  SquaresFour, Briefcase, UsersThree, Gear, ListChecks, ClipboardText,
  CurrencyInr, ChartLineUp, Truck, SignOut, Wrench, List, FileText, CalendarBlank,
} from "@phosphor-icons/react";
import { NAV_ITEMS } from "@/config/schemas";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canAccessEntity, isAdmin, accessLevel, homePath } from "@/lib/permissions";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const ICONS = { SquaresFour, Briefcase, UsersThree, Gear, ListChecks, ClipboardText, CurrencyInr, ChartLineUp, Truck, FileText, CalendarBlank };

function SidebarContent({ user, onNavigate, onLogout, alerts }) {
  const navigate = useNavigate();
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const lvl = accessLevel(user?.role);
  const goHome = () => { navigate(homePath(user?.role)); onNavigate?.(); };
  let visible;
  if (lvl === "operator") {
    visible = [{ key: "log", label: "Log Work", icon: "ClipboardText", path: "/forms/log-production" }];
  } else {
    visible = NAV_ITEMS.filter((item) => canAccessEntity(user?.role, item.key));
    if (lvl === "owner") {
      visible = [...visible,
        { key: "users", label: "Manage Users", icon: "UsersThree", path: "/users" },
        { key: "audit", label: "Audit Log", icon: "FileText", path: "/audit" }];
    }
  }
  return (
    <div className="h-full flex flex-col bg-slate-900 text-slate-300">
      <button
        onClick={goHome}
        className="px-5 py-5 border-b border-slate-800 flex items-center gap-2.5 w-full text-left hover:bg-slate-800/60 transition-colors"
        data-testid="brand-home-sidebar"
        title="Go to dashboard"
      >
        <div className="rounded-md bg-white px-2 py-1.5 shrink-0">
          <img src="/apt-logo.jpg" alt="Apt Enterprises" className="h-5 w-auto" />
        </div>
        <div className="text-[11px] text-slate-400 leading-tight whitespace-nowrap">Tool Room ERP</div>
      </button>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visible.map((item) => {
          const Icon = ICONS[item.icon];
          return (
            <NavLink
              key={item.key}
              to={item.path}
              end={item.path === "/"}
              onClick={onNavigate}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              <span className="truncate flex-1">{item.label}</span>
              {item.key === "external" && alerts?.overdue_external > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="overdue-badge">
                  {alerts.overdue_external}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-slate-800">
        <div className="flex items-center gap-3 px-2 mb-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-blue-600 text-white text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm text-white font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-400 truncate">{user?.role}</div>
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
        >
          <SignOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const admin = isAdmin(user?.role);

  const { data: alerts } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () => (await api.get("/alerts")).data,
    enabled: admin,
    refetchInterval: 60000,
  });

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 fixed h-full z-20">
        <SidebarContent user={user} onNavigate={() => {}} onLogout={doLogout} alerts={alerts} />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 bg-slate-900 text-white flex items-center justify-between px-4 z-30">
        <button
          onClick={() => navigate(homePath(user?.role))}
          className="flex items-center gap-2.5 -ml-1 px-1 py-1 rounded hover:bg-slate-800 transition-colors"
          data-testid="brand-home-mobile"
        >
          <div className="rounded-md bg-white px-2 py-1">
            <img src="/apt-logo.jpg" alt="Apt Enterprises" className="h-4 w-auto" />
          </div>
          <span className="text-xs text-slate-300 tracking-tight">Tool Room ERP</span>
        </button>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="p-2 -mr-2 relative" data-testid="mobile-menu-btn" aria-label="Open menu">
              <List className="h-6 w-6" weight="bold" />
              {alerts?.overdue_external > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />}
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 border-0">
            <SidebarContent user={user} onNavigate={() => setOpen(false)} onLogout={doLogout} alerts={alerts} />
          </SheetContent>
        </Sheet>
      </header>

      {/* Main */}
      <main className="lg:ml-64 px-4 md:px-8 pt-20 pb-8 lg:pt-6">{children}</main>
    </div>
  );
}
